import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { apiRequest } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Search, MoreHorizontal } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface User {
  id: number;
  username: string;
  email?: string;
  role: string;
  isActive: boolean;
  verified?: boolean;
}

export function UsersTab() {
  const {
    data: users,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => {
      const response = await apiRequest("/api/admin/users");
      return response as User[];
    },
  });

  const { toast } = useToast();
  const [userToModify, setUserToModify] = useState<User | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isActiveDialogOpen, setIsActiveDialogOpen] = useState(false);
  const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] = useState(false);
  const [isVerifyAccountDialogOpen, setIsVerifyAccountDialogOpen] = useState(false);
  const [isSendResetLinkDialogOpen, setIsSendResetLinkDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");

  // Update user role mutation
  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: number; role: string }) => {
      const response = await apiRequest(`/api/admin/update-role/${userId}`, {
        method: "POST",
        body: JSON.stringify({ role }),
      });
      return response as { success: boolean };
    },
    onSuccess: () => {
      toast({
        title: "User role updated",
        description: "The user role has been updated successfully",
      });
      refetch();
      setIsDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error updating user role",
        description: error.message ?? "An error occurred while updating the user role",
        variant: "destructive",
      });
    },
  });

  // Update user active status mutation
  const updateUserActiveMutation = useMutation({
    mutationFn: async ({
      userId,
      isActive,
    }: {
      userId: number;
      isActive: boolean;
    }) => {
      const response = await apiRequest(`/api/admin/update-user-status/${userId}`, {
        method: "POST",
        body: JSON.stringify({ isActive }),
      });
      return response as { success: boolean };
    },
    onSuccess: () => {
      toast({
        title: "User status updated",
        description: `User has been ${userToModify?.isActive ? "disabled" : "enabled"} successfully`,
      });
      refetch();
      setIsActiveDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error updating user status",
        description: error.message ?? "An error occurred while updating the user status",
        variant: "destructive",
      });
    },
  });

  // Reset user password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: async ({
      userId,
      password,
    }: {
      userId: number;
      password: string;
    }) => {
      const response = await apiRequest(`/api/admin/reset-password/${userId}`, {
        method: "POST",
        body: JSON.stringify({ password }),
      });
      return response as { success: boolean };
    },
    onSuccess: () => {
      toast({
        title: "Password reset successful",
        description: `Password has been reset for ${userToModify?.username}`,
      });
      refetch();
      setIsResetPasswordDialogOpen(false);
      setNewPassword("");
    },
    onError: (error: any) => {
      toast({
        title: "Error resetting password",
        description: error.message ?? "An error occurred while resetting the password",
        variant: "destructive",
      });
    },
  });

  // Mark account as verified mutation
  const verifyAccountMutation = useMutation({
    mutationFn: async ({ userId }: { userId: number }) => {
      const response = await apiRequest(`/api/admin/verify-account/${userId}`, {
        method: "POST",
      });
      return response as { success: boolean };
    },
    onSuccess: () => {
      toast({
        title: "Account verified",
        description: `${userToModify?.username}'s account has been marked as verified`,
      });
      refetch();
      setIsVerifyAccountDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error verifying account",
        description: error.message ?? "An error occurred while verifying the account",
        variant: "destructive",
      });
    },
  });

  // Send password reset link mutation
  const sendResetLinkMutation = useMutation({
    mutationFn: async ({ userId }: { userId: number }) => {
      const response = await apiRequest(`/api/admin/send-reset-link/${userId}`, {
        method: "POST",
      });
      return response as { success: boolean };
    },
    onSuccess: () => {
      toast({
        title: "Reset link sent",
        description: `Password reset link has been sent to ${userToModify?.email}`,
      });
      refetch();
      setIsSendResetLinkDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error sending reset link",
        description: error.message ?? "An error occurred while sending the reset link",
        variant: "destructive",
      });
    },
  });

  const handleRoleUpdate = (role: string) => {
    if (!userToModify) return;
    updateRoleMutation.mutate({ userId: userToModify.id, role });
  };

  const handleToggleUserActive = () => {
    if (!userToModify) return;
    updateUserActiveMutation.mutate({
      userId: userToModify.id,
      isActive: !userToModify.isActive,
    });
  };

  // Handle reset password
  const handleResetPassword = () => {
    if (!userToModify || !newPassword) return;
    resetPasswordMutation.mutate({
      userId: userToModify.id,
      password: newPassword,
    });
  };

  // Handle verify account
  const handleVerifyAccount = () => {
    if (!userToModify) return;
    verifyAccountMutation.mutate({
      userId: userToModify.id,
    });
  };

  // Handle send password reset link
  const handleSendResetLink = () => {
    if (!userToModify) return;
    sendResetLinkMutation.mutate({
      userId: userToModify.id,
    });
  };

  // Filter users based on search query and filters
  const filteredUsers = users
    ? users.filter((user: any) => {
        // Search filter
        const matchesSearch = [
          user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false,
          user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false,
          user.id?.toString().includes(searchQuery) ?? false
        ].some(Boolean);

        // Role filter
        const matchesRole = roleFilter === "all" || user.role === roleFilter;

        // Active status filter
        const matchesActive =
          activeFilter === "all" ||
          (activeFilter === "active" && user.isActive) ||
          (activeFilter === "disabled" && !user.isActive);

        return matchesSearch && matchesRole && matchesActive;
      })
    : [];

  if (isLoading) {
    return <div className="text-center py-8">Loading users...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between mb-4">
        <h2 className="text-2xl font-semibold">User Management</h2>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search users by name, email or ID..."
              className="pl-9 w-full rounded-md border border-input bg-slate-800 py-2 text-sm text-white ring-offset-background placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-2">
          <select
            className="rounded-md border border-input bg-slate-800 px-3 py-2 text-sm text-white ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="all">All Roles</option>
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="player">Player</option>
          </select>

          <select
            className="rounded-md border border-input bg-slate-800 px-3 py-2 text-sm text-white ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>
      </div>

      {!users || users.length === 0 ? (
        <div className="text-center py-8 border rounded-lg bg-muted/50">
          No users found
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-8 border rounded-lg bg-muted/50">
          No users match your search criteria
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-2">ID</th>
                <th className="text-left p-2">Username</th>
                <th className="text-left p-2">Email</th>
                <th className="text-left p-2">Role</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user: any) => (
                <tr
                  key={user.id}
                  className={`border-t hover:bg-muted/50 ${!user.isActive ? "opacity-60" : ""}`}
                >
                  <td className="p-2">{user.id}</td>
                  <td className="p-2">{user.username}</td>
                  <td className="p-2">{user.email ?? "Not provided"}</td>
                  <td className="p-2">
                    <Badge
                      className={
                        user.role === "admin"
                          ? "bg-red-600 hover:bg-red-700 text-white"
                          : user.role === "manager"
                            ? "bg-blue-600 hover:bg-blue-700 text-white"
                            : "bg-emerald-600 hover:bg-emerald-700 text-white"
                      }
                    >
                      {user.role}
                    </Badge>
                  </td>
                  <td className="p-2">
                    <Badge
                      className={
                        user.isActive
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                          : "bg-red-600 hover:bg-red-700 text-white"
                      }
                    >
                      {user.isActive ? "Active" : "Disabled"}
                    </Badge>
                  </td>
                  <td className="p-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem
                          onClick={() => {
                            setUserToModify(user);
                            setIsDialogOpen(true);
                          }}
                        >
                          Change Role
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setUserToModify(user);
                            setIsActiveDialogOpen(true);
                          }}
                        >
                          {user.isActive ? "Disable User" : "Enable User"}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setUserToModify(user);
                            setIsResetPasswordDialogOpen(true);
                          }}
                        >
                          Reset Password
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setUserToModify(user);
                            setIsVerifyAccountDialogOpen(true);
                          }}
                        >
                          Mark as Verified
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setUserToModify(user);
                            setIsSendResetLinkDialogOpen(true);
                          }}
                        >
                          Send Password Reset Link
                        </DropdownMenuItem>
                        <DropdownMenuItem>View Profile</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Role Change Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>
              Update the role for user {userToModify?.username}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select a new role</Label>
              <RadioGroup
                defaultValue={userToModify?.role}
                onValueChange={(value) => handleRoleUpdate(value)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="player" id="player" />
                  <Label htmlFor="player">Player</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="manager" id="manager" />
                  <Label htmlFor="manager">Manager</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="admin" id="admin" />
                  <Label htmlFor="admin">Admin</Label>
                </div>
              </RadioGroup>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => setIsDialogOpen(false)}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Active Status Dialog */}
      <AlertDialog
        open={isActiveDialogOpen}
        onOpenChange={setIsActiveDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {userToModify?.isActive
                ? "Disable User Account"
                : "Enable User Account"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {userToModify?.isActive
                ? `This will prevent ${userToModify?.username} from logging in. They won't be able to access the platform until their account is enabled again.`
                : `This will allow ${userToModify?.username} to log in and access the platform again.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleToggleUserActive}
              className={
                userToModify?.isActive ? "bg-red-600 hover:bg-red-700" : ""
              }
            >
              {userToModify?.isActive ? "Disable User" : "Enable User"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Dialog */}
      <Dialog 
        open={isResetPasswordDialogOpen}
        onOpenChange={setIsResetPasswordDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset User Password</DialogTitle>
            <DialogDescription>
              Enter a new password for {userToModify?.username}. 
              This will immediately change their password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full rounded-md border border-input bg-slate-800 py-2 px-3 text-sm text-white ring-offset-background placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Enter new password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsResetPasswordDialogOpen(false);
              setNewPassword("");
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleResetPassword}
              disabled={!newPassword}
            >
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Account as Verified Dialog */}
      <AlertDialog
        open={isVerifyAccountDialogOpen}
        onOpenChange={setIsVerifyAccountDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Mark Account as Verified
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will mark {userToModify?.username}'s account as verified, 
              allowing them to access all verified-only features.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleVerifyAccount}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Verify Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send Password Reset Link Dialog */}
      <AlertDialog
        open={isSendResetLinkDialogOpen}
        onOpenChange={setIsSendResetLinkDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Send Password Reset Link
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will send a password reset link to {userToModify?.email || "the user's email"}. 
              The user will be able to set a new password using this link.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSendResetLink}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Send Reset Link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}